import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { useTheme } from '@material-ui/core'
import { Text } from './Text'
import { Button } from './Button'

// Today / Tomorrow / Yesterday / "dddd, D MMMM YYYY" — the long form reads well as a
// standalone agenda heading (the sidebar/timeline dupes use the short "ddd, D MMM YYYY").
const dayLabel = (raw) => {
  const d = moment(raw)
  if (!raw || !d.isValid()) return 'No date'
  if (d.isSame(moment(), 'day')) return `Today · ${d.format('ddd, D MMM')}`
  if (d.isSame(moment().add(1, 'day'), 'day')) return `Tomorrow · ${d.format('ddd, D MMM')}`
  if (d.isSame(moment().subtract(1, 'day'), 'day')) return `Yesterday · ${d.format('ddd, D MMM')}`
  return d.format('dddd, D MMMM YYYY')
}

const NO_DATE = '__nodate__'

// A centered, width-limited, date-grouped list. Buckets `items` by the calendar day of
// `getDate(item)`, renders one large date header per bucket, then hands each row to
// `renderItem(item, { index, isFirst, isLast, groupLabel })` — the caller fully controls
// how a row looks, so this stays reusable for any item type. Rows get NO wrapper padding:
// the header and each row share the container's left edge, so a row whose own left column
// is an icon (timeline style) sits with its icon under the header text.
//
//   getDate   (item) => date-ish value used for grouping + intra-group sort
//   renderItem(item, meta) => node
//   getKey    (item, i) => react key (defaults to "<groupKey>:<i>")
//   order     'asc' (soonest first, the default) | 'desc'
//   maxWidth  content column cap in px; default 760
//   align     'center' (default, margin: 0 auto) | 'left' (flush to the container's
//     left edge instead of centered — e.g. TaskPage's wider grid layout)
//   onLoadMore / hasMore / loadingMore — infinite scroll: a bottom sentinel calls
//     onLoadMore when it nears the viewport while hasMore and not already loadingMore.
//   loadMoreDedupeKey — value the infinite-scroll guard compares instead of `items.length`
//     to decide "did this load actually add anything" (defaults to `items.length`, today's
//     behaviour). Needed when the caller passes a client-filtered `items` array whose
//     length can coincidentally repeat across two real, distinct fetched pages.
//   bucketize (items) => [{ key, label, items, ... }] — when given, fully replaces the
//     built-in per-day grouping (and its ordering); the caller owns groups + order.
//   renderGroupHeader (group) => node — when given, replaces the default big date header.
//   maxItemsPerGroup — caps each group's rendered rows, with an inline "Show N more" /
//     "Show less" toggle per group (same idiom as the Participants list elsewhere in the
//     app). Omit for unlimited (today's behaviour).
//   currentGroupKey — the "Today" day key ('YYYY-MM-DD'), per-day grouping only. That group
//     is always rendered (as an empty group showing `emptyGroupText` when it has no rows),
//     its default header title takes the primary colour, and it is the target of the scroll
//     below. Omit when the range doesn't contain today.
//   scrollRequest — any value; whenever it changes (and once on mount) the list scrolls the
//     currentGroupKey group to the top of the scroll area, or the list's own top when there
//     is no such group. `scrollReady` (default true) defers that until the caller says the
//     rows have settled, so a still-loading list doesn't shift the target after the scroll.
//     `getScrollOffset` () => px reserves room for sticky headers above the list.
export const AgendaList = ({
  items = [],
  getDate,
  renderItem,
  getKey,
  emptyText = 'Nothing to show.',
  isFetching = false,
  order = 'asc',
  maxWidth = 760,
  align = 'center',
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  loadMoreDedupeKey,
  bucketize,
  renderGroupHeader,
  maxItemsPerGroup,
  currentGroupKey,
  emptyGroupText = 'Nothing scheduled.',
  scrollRequest,
  scrollReady = true,
  getScrollOffset,
}) => {
  const dir = order === 'desc' ? -1 : 1
  const theme = useTheme()

  const rootRef = useRef(null)
  const pendingScrollRef = useRef(true)
  const hasScrolledRef = useRef(false)
  const getScrollOffsetRef = useRef(getScrollOffset)
  getScrollOffsetRef.current = getScrollOffset

  const sentinelRef = useRef(null)
  const loadMoreRef = useRef(onLoadMore)
  loadMoreRef.current = onLoadMore
  // Guards against re-requesting the same page: only fire once per distinct dedupe value.
  const requestedAtRef = useRef(undefined)

  const [expandedGroups, setExpandedGroups] = useState(() => new Set())
  const toggleGroup = (key) => setExpandedGroups((prev) => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })

  // IntersectionObserver against the viewport (root: null) works regardless of which
  // ancestor actually scrolls the agenda. Re-armed whenever the dedupe value changes so a
  // still-visible sentinel keeps paging until the viewport is filled / hasMore is false.
  const dedupeValue = loadMoreDedupeKey !== undefined ? loadMoreDedupeKey : items.length
  useEffect(() => {
    if (!hasMore) { requestedAtRef.current = undefined; return }
    const el = sentinelRef.current
    if (!el || loadingMore || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return
      if (!loadMoreRef.current || requestedAtRef.current === dedupeValue) return
      requestedAtRef.current = dedupeValue
      loadMoreRef.current()
    }, { rootMargin: '600px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [hasMore, loadingMore, dedupeValue])

  // Scroll to the current (Today) group. A new `scrollRequest` arms it; it then fires once
  // `scrollReady` is true (the effect below re-runs when that flips).
  useEffect(() => { pendingScrollRef.current = true }, [scrollRequest])
  useEffect(() => {
    if (!pendingScrollRef.current || !scrollReady) return
    const root = rootRef.current
    if (!root) return
    pendingScrollRef.current = false
    const target = (currentGroupKey && root.querySelector(`[data-group-key="${currentGroupKey}"]`)) || root
    // scroll-margin keeps the target clear of the sticky page header + filter bar.
    const offset = getScrollOffsetRef.current ? getScrollOffsetRef.current() : 0
    target.style.scrollMarginTop = `${offset + 8}px`
    target.scrollIntoView({ block: 'start', behavior: hasScrolledRef.current ? 'smooth' : 'auto' })
    hasScrolledRef.current = true
  }, [scrollRequest, scrollReady]) // eslint-disable-line react-hooks/exhaustive-deps

  let groups = []
  if (bucketize) {
    groups = bucketize(items || []) || []
  } else {
    const byKey = new Map()
    if (currentGroupKey) {
      const g = { key: currentGroupKey, label: dayLabel(currentGroupKey), items: [] }
      byKey.set(currentGroupKey, g)
      groups.push(g)
    }
    ;(items || []).forEach((item) => {
      const raw = getDate ? getDate(item) : item?.date
      const valid = raw && moment(raw).isValid()
      const key = valid ? moment(raw).format('YYYY-MM-DD') : NO_DATE
      let g = byKey.get(key)
      if (!g) {
        g = { key, label: dayLabel(valid ? raw : null), items: [] }
        byKey.set(key, g)
        groups.push(g)
      }
      g.items.push(item)
    })

    groups.sort((a, b) => {
      if (a.key === NO_DATE) return 1
      if (b.key === NO_DATE) return -1
      return a.key === b.key ? 0 : dir * (a.key < b.key ? -1 : 1)
    })
    groups.forEach((g) => g.items.sort((a, b) => {
      const da = getDate ? getDate(a) : null
      const db = getDate ? getDate(b) : null
      if (!da || !db) return 0
      return dir * moment(da).diff(moment(db))
    }))
  }

  const isEmpty = !items || items.length === 0

  return (
    <div ref={rootRef} style={{ maxWidth, width: '100%', margin: align === 'left' ? '0' : '0 auto', padding: '4px 0px 48px', boxSizing: 'border-box' }}>
      {isEmpty && groups.length === 0 && (
        <Text color="textMuted" style={{ padding: '24px 0', display: 'block' }}>
          {isFetching ? 'Loading…' : emptyText}
        </Text>
      )}
      {groups.map((group) => {
        const isExpanded = expandedGroups.has(group.key)
        const isCapped = Boolean(maxItemsPerGroup) && !isExpanded && group.items.length > maxItemsPerGroup
        const visibleItems = isCapped ? group.items.slice(0, maxItemsPerGroup) : group.items
        return (
          <div key={group.key} data-group-key={group.key}>
            {renderGroupHeader ? renderGroupHeader(group) : (
              <Text
                variant="h6"
                component="div"
                style={{
                  fontWeight: 700,
                  padding: '24px 0 12px',
                  display: 'block',
                  color: group.key === currentGroupKey ? theme.palette.primary.main : undefined,
                }}
              >
                {group.label}
              </Text>
            )}
            {group.items.length === 0 && group.key === currentGroupKey && (
              <Text color="textMuted" style={{ display: 'block', paddingBottom: 12 }}>
                {isFetching ? 'Loading…' : emptyGroupText}
              </Text>
            )}
            {visibleItems.map((item, i) => (
              <React.Fragment key={getKey ? getKey(item, i) : `${group.key}:${i}`}>
                {renderItem(item, {
                  index: i,
                  isFirst: i === 0,
                  isLast: i === group.items.length - 1,
                  groupLabel: group.label,
                })}
              </React.Fragment>
            ))}
            {maxItemsPerGroup && group.items.length > maxItemsPerGroup && (
              <Button variant="text" size="small" color="primary" onClick={() => toggleGroup(group.key)}>
                {isExpanded ? 'Show less' : `Show ${group.items.length - maxItemsPerGroup} more`}
              </Button>
            )}
          </div>
        )
      })}
      {hasMore && onLoadMore && <div ref={sentinelRef} style={{ height: 1 }} aria-hidden />}
      {loadingMore && (
        <Text color="textMuted" style={{ padding: '16px 0', display: 'block', textAlign: 'center' }}>
          Loading more…
        </Text>
      )}
    </div>
  )
}

export default AgendaList
